import {
    ICartItemData,
    ICartItemsByFare,
    ICartItemsByGroup,
    ICartItemsByProduct,
} from "@interfaces";
import _ from "lodash";
import { useState, useMemo, useEffect } from "react";
import { PointCommon } from "@components";
import { breakpoint } from "@utilities";
import styled from "styled-components";
import { ItemsByProductHeader } from "./ItemByProductHeader";
import {
    buildItemsForNewQuantity,
    formatBookingDatesByProductClass,
    getTotalRRP,
    groupCartItemsByProduct,
} from "../checkoutHelpers";
import { CheckoutItemsActions } from "./CheckoutActions";
import { WarnToast } from "src/Components/toasts/WarnToast";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { useCheckout } from "../CheckoutContext";

interface ICartItemByGroupProps extends ICartItemsByGroup {
    productClass: string;
    onItemsChange: (items: ICartItemData[]) => void;
    handleDeleteItems?: (cartItemIds: string[]) => void;
    originalItems: ICartItemData[];
}

export const CartItemGroupComponent = ({
    items,
    originalItems,
    productClass,
    onItemsChange,
    handleDeleteItems,
}: ICartItemByGroupProps) => {
    const quantity = useMemo(() => items[0]?.booking_quantity || 0, [items]);
    const maxAvailable = Math.max(...items.map((item) => item.availability.NumAvailable));
    const price = getTotalRRP(items);

    useEffect(() => {
        if (_.uniq(items.map((i) => i.booking_quantity)).length > 1) {
            console.warn(`Different quantities under same group`, items);
        }
    }, [items]);

    const handleQuantityUpdate = (newQuantity: number) => {
        if (newQuantity < 1) {
            if (handleDeleteItems) {
                handleDeleteItems(
                    items.filter((i) => !_.isNil(i) && !_.isNil(i.id)).map((i) => i.id.toString()),
                );
            } else {
                WarnToast({ Message: "Minimun quantity reached" });
            }
            return;
        }
        if (newQuantity > maxAvailable) {
            WarnToast({ Message: `Not allowed than max available` });
            return;
        }
        onItemsChange(buildItemsForNewQuantity(items, newQuantity, originalItems));
    };

    return (
        <CartItemRowContainer>
            <DateText>
                {formatBookingDatesByProductClass(
                    items.map((i) => i.booking_date),
                    productClass,
                )}
            </DateText>
            <PriceContainerDiv>
                <div className="price">
                    <p>Price</p> {price && <span>${price.totalRRP.toFixed(2)}</span>}
                </div>
                <div className="quantity">
                    Qty
                    <PointCommon type="button" onClick={() => handleQuantityUpdate(quantity - 1)}>
                        -
                    </PointCommon>
                    <span>{quantity}</span>
                    <PointCommon type="button" onClick={() => handleQuantityUpdate(quantity + 1)}>
                        +
                    </PointCommon>
                </div>
            </PriceContainerDiv>
        </CartItemRowContainer>
    );
};

const CartItemRowContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 10px;
`;

const PriceContainerDiv = styled.div`
    border: 1px solid #c3c9d0;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    padding: 8px 14px;
    align-items: center;

    .price {
        display: flex;
        flex-direction: column;
        color: #6e7375;
        font-size: 12px;
        font-family: Plus Jakarta Sans;
        span {
            color: #000;
            font-size: 18px;
        }
    }

    .quantity {
        color: #6e7375;
        font-size: 12px;
        display: flex;
        align-items: center;
        gap: 10px;
        span {
            font-size: 20px;
            color: #1f2937;
            width: 18px;
            text-align: center;
        }
    }
`;
const DateText = styled.p`
    font-size: 12px;
    font-weight: 600;
    font-family: Plus Jakarta Sans;
    color: #232b2b;
    margin: 4px 0px 8px 0px;
    text-decoration: underline;
    text-underline-offset: 3px;
`;

interface ICartItemsByFareProps extends ICartItemsByFare {
    productClass: string;
    onItemsChange: (items: ICartItemData[]) => void;
    handleDeleteItems?: (cartItemIds: string[]) => void;
    originalItemsByGroup: ICartItemsByGroup[];
}

const CartItemsByFareComponent = (props: ICartItemsByFareProps) => {
    return (
        <div>
            <div className="fareName">{props.name}</div>
            {props.itemsByGroup.map((itemByGroup, groupIndex) => {
                return (
                    <CartItemGroupComponent
                        key={groupIndex}
                        group_id={itemByGroup.group_id}
                        items={itemByGroup.items}
                        originalItems={props.originalItemsByGroup[groupIndex]?.items || []}
                        productClass={props.productClass}
                        onItemsChange={props.onItemsChange}
                        handleDeleteItems={props.handleDeleteItems}
                    />
                );
            })}
        </div>
    );
};

interface ICartItemByProductProps extends ICartItemsByProduct {
    handleDeleteProduct?: (productId: string) => void;
    onItemsChange: (items: ICartItemData[]) => void;
    handleDeleteItems?: (cartItemIds: string[]) => void;
    originalItemsByFare: ICartItemsByFare[];
    supplierName: string;
}

const CartItemsByProduct = (props: ICartItemByProductProps) => {
    return (
        <Info className="infoWrapper">
            <div className="info">
                <ItemsByProductHeader
                    {...props}
                    {...(props.handleDeleteProduct && {
                        onDelete: () => props.handleDeleteProduct!(props.productId.toString()),
                        showDeleteButton: true,
                    })}
                />
                <Border />
                {props.itemsByFare.map((itemsByFare, fareIndex) => {
                    return (
                        <CartItemsByFareComponent
                            key={fareIndex}
                            id={itemsByFare.id}
                            name={itemsByFare.name}
                            productClass={props.productClass}
                            itemsByGroup={itemsByFare.itemsByGroup}
                            originalItemsByGroup={
                                props.originalItemsByFare[fareIndex]?.itemsByGroup || []
                            }
                            onItemsChange={props.onItemsChange}
                            handleDeleteItems={props.handleDeleteItems}
                            bookingSystem={itemsByFare.bookingSystem}
                        />
                    );
                })}
            </div>
        </Info>
    );
};

interface ICheckoutStep1Props {
    handleDeleteItems?: (cartItemIds: string[]) => void;
    handleDeleteProduct?: (productId: string) => void;
    handleSaveItems: (items: ICartItemData[]) => Promise<void>;
    handleDeleteCart?: () => void;
}

export const CheckoutStep1 = ({
    handleDeleteItems,
    handleDeleteProduct,
    handleSaveItems,
    handleDeleteCart,
}: ICheckoutStep1Props) => {
    const { items, setItems } = useCheckout();
    const originalItemsByProduct = useMemo(() => groupCartItemsByProduct(items), [items]);
    const itemsByProduct = useMemo(() => groupCartItemsByProduct(items), [items]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleGroupItemsChange = (updatedItems: ICartItemData[]) => {
        setItems(
            items.map((cartItem) => {
                const updateItem = updatedItems.find((item) => item.id === cartItem.id);
                if (updateItem) {
                    return updateItem;
                }
                return cartItem;
            }),
        );
    };

    return (
        <Container>
            <div className="cartWrapper">
                {handleDeleteCart && <RemoveAll onClick={handleDeleteCart}>Remove All</RemoveAll>}{" "}
                {itemsByProduct.map((itemByProduct, productIndex) => {
                    return (
                        <CartItemsByProduct
                            key={itemByProduct.productId.toString()}
                            productId={itemByProduct.productId}
                            name={itemByProduct.name}
                            productClass={itemByProduct.productClass}
                            productImagePath={itemByProduct.productImagePath}
                            itemsByFare={itemByProduct.itemsByFare}
                            {...(handleDeleteProduct && {
                                handleDeleteProduct: (pid) => handleDeleteProduct(pid),
                            })}
                            supplierName={itemByProduct.supplierName || ""}
                            startLocationName={itemByProduct.startLocationName}
                            onItemsChange={handleGroupItemsChange}
                            handleDeleteItems={handleDeleteItems}
                            originalItemsByFare={
                                originalItemsByProduct[productIndex]?.itemsByFare || []
                            } // Add missing prop
                        />
                    );
                })}
            </div>
            <div className="totalPriceContainer">
                <CheckoutItemsActions
                    btnTitle="Checkout"
                    isSubmitting={isSubmitting}
                    onBtnClick={async () => {
                        try {
                            setIsSubmitting(true);
                            await handleSaveItems(items);
                        } catch (error) {
                            console.error("Error saving items:", error);
                            ErrorToast({ Message: "Failed to save items" });
                        } finally {
                            setIsSubmitting(false);
                        }
                    }}
                    page="cart"
                />
            </div>
        </Container>
    );
};

export const Info = styled.div`
    background-color: ${(props) => props.theme.colors.gray_6};
    padding-bottom: 0 !important;
    margin-bottom: 16px;
    .info {
        display: flex;
        flex-direction: column;
        background-color: #fff;
        padding: 16px;
        border-radius: 8px;
        .main {
            display: flex;
            gap: 12px;
        }
        .img {
            position: relative;
            cursor: pointer;
            height: 64px;
            width: 64px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            img {
                height: 100%;
                width: 100%;
                object-fit: cover;
                border-radius: 8px;
            }
        }
        .info-container {
            display: flex;
            justify-content: space-between;
            width: 100%;
            gap: 10px;
        }
        .price-container {
            border: 1px solid #c3c9d0;
            border-radius: 8px;
            display: flex;
            justify-content: space-between;
            padding: 8px 20px;
            align-items: center;
            .price {
                display: flex;
                flex-direction: column;
                color: #6e7375;
                font-size: 12px;
                span {
                    color: #000;
                    font-size: 18px;
                }
            }
            .quantity {
                color: #6e7375;
                font-size: 12px;
                display: flex;
                align-items: center;
                gap: 10px;
                span {
                    font-size: 20px;
                    color: #1f2937;
                    width: 18px;
                    text-align: center;
                }
            }
        }
        .fareName {
            font-size: 18px;
            font-weight: 600;
        }
    }
    .name-container {
        display: flex;
        flex-direction: column;
        gap: 6px;
        .title {
            width: 100%;
            font-size: 14px;
            font-weight: 600;
            line-height: 23px;
            cursor: pointer;
            color: #131214;
            text-decoration: underline;
            text-underline-offset: 3px;
            @media (min-width: 375px) {
                width: 200px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            @media (min-width: 425px) {
                width: 250px;
            }
            ${breakpoint.breakLarge`
                font-size: 18px;
                width: auto;
                overflow: visible;
                text-overflow: clip;
                white-space: normal;
            `}
        }
        .sub-title {
            color: #6e7375;
            font-size: 12px;
            line-height: 14px;
            @media (min-width: 375px) {
                width: 200px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            @media (min-width: 425px) {
                width: 250px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            ${breakpoint.breakLarge`
                width: auto;
                overflow: visible;
                text-overflow: clip;
                white-space: normal;
            `}
        }
        .location {
            display: flex;
            align-items: center;
            gap: 6px;
            color: #3c3c3c;
            font-size: 12px;
            font-weight: 500;
        }
    }
`;

export const Border = styled.div`
    border-top: 1px solid #e6e9eb;
    margin: 10px 0px 14px 0px;
`;

export const Container = styled.div`
    display: flex;
    font-family: Poppins;
    .cartWrapper {
        position: relative;
        display: flex;
        flex-direction: column;
        padding-right: 16px;
        ${breakpoint.breakOnlyMobile`
            padding-bottom: 100px;
        `}
    }
    ${breakpoint.breakTablet`
        & > .cartWrapper {
            width: ${(p) => p.theme.widths.wide_450};
        }
    `}
    ${breakpoint.breakIpadPro`
        & > .cartWrapper {
            width: ${(p) => p.theme.widths.wide_650};
        }
    `}
    ${breakpoint.breakLarge`
        & > .cartWrapper {
            width: ${(p) => p.theme.widths.wide_825};
        }
    `}
    ${breakpoint.breakOnlyMobile`
        display: block;
        padding: 16px 0px 0px 16px;
    `}
    .totalPriceContainer {
        ${breakpoint.breakLarge`
            width: 300px;
        `}
        ${breakpoint.breakOnlyMobile`
            position: fixed;
            bottom: 0;
            left: 0;
            background-color: white;
            z-index: 100;
            width: 100%;
        `}
    }
`;

const RemoveAll = styled.button`
    position: absolute;
    top: -30px;
    right: 20px;
    color: #31b4b9;
    font-weight: 700;
    font-size: 16px;
`;
